import { isRecord } from 'utils/objectUtils';
import type {
  Rule,
  RuleCriteriaGroup,
  RuleApplyBehavior,
  RuleApplyNotification,
} from '@billing/validation/rules';
import {
  AssertionField,
  ScopeMode,
  PatientAgeOperator,
  ServiceStartTimeOperator,
  ServiceStartDayOperator,
  supportsTariffExclusions,
  whenClauseSchema,
  RuleMode,
  RuleSeverity,
  WhenLogic,
} from '@billing/validation/rules';
import type { RuleCondition } from './constants';
import type {
  AssertionRow,
  ApplyUpdateRow,
  RuleModeSelection,
  WhenClause,
} from './ruleSchemas';
import { getBillingHistoryWindow } from './ruleUtils';
import { timeBasedApplyFields } from './constants';

interface RuleCriteriaConditions {
  operator: WhenLogic;
  conditions: (RuleCondition | null)[];
}

interface CompleteRuleCriteriaConditions extends RuleCriteriaConditions {
  conditions: RuleCondition[];
}

export const hasCompleteRuleCriteria = (
  groups: RuleCriteriaConditions[]
): groups is CompleteRuleCriteriaConditions[] =>
  groups.length > 0 &&
  groups.every(
    (group) =>
      group.conditions.length > 0 &&
      group.conditions.every((condition) => condition !== null)
  );

interface RuleBodyInput {
  assertions: AssertionRow[];
  applyIntervalMinutes: string;
  applyUpdates: ApplyUpdateRow[];
  applyBehavior?: RuleApplyBehavior;
  applyNotification?: RuleApplyNotification;
  effectiveApplyPriority: number;
  effectiveTransformPriority: number;
  effectiveFlagLabel: string;
  effectiveFlagMessage: string;
  effectiveRuleName: string;
  effectiveRuleMessage: string;
  eventCode: string;
  eventType: string;
  firstScopeValues: string[];
  firstScopeMode: ScopeMode;
  flagSeverity: string;
  ruleMode: RuleModeSelection;
  severity: RuleSeverity;
  timeCodeAfterMidnight: string;
  timeCodeEvening: string;
  timeCodeWeekend: string;
  transformAppendFlags: boolean;
  transformApplyPercentages: boolean;
  transformCollapseByCode: boolean;
  validationFailureConditions: RuleCondition[];
  whenGroups: RuleCriteriaGroup[];
  criteriaConditions: RuleCriteriaConditions[];
  whenLogic: WhenLogic;
}

interface RuleBodyChanges {
  action: boolean;
  criteria: boolean;
  outcome: boolean;
  checks?: boolean;
}

const managedEventParams = new Set([
  'action',
  'category',
  'code',
  'severity',
  'userMessage',
  'apply',
  'flagLabel',
  'flagMessage',
  'flagSeverity',
]);
const managedApplyFields = new Set([
  'field',
  'value',
  'behavior',
  'notification',
  'writePolicy',
  'updates',
  'priority',
  'intervalMinutes',
  'targetTariffs',
  'targetMatchMode',
  'appendTimeFlagsToProviderNotes',
  'collapseTariffsByCode',
  'applyBaseTariffPercentages',
  'timeCodes',
]);
const managedCheckFields = new Set([
  'id',
  'field',
  'operator',
  'valuesText',
  'billingHistory',
  'window',
  'sameProvider',
]);
const managedBillingWindowFields = new Set(['type', 'amount', 'unit']);
const managedCriteriaFields = new Set(['operator', 'clauses', 'groups']);

const managedCriteriaClauseFields = new Set(
  Object.keys(whenClauseSchema.shape)
);

const toRecord = (value: unknown): Record<string, unknown> =>
  isRecord(value) ? Object.fromEntries(Object.entries(value)) : {};

const omitManagedFields = (
  value: unknown,
  fields: ReadonlySet<string>
): Record<string, unknown> =>
  Object.fromEntries(
    Object.entries(isRecord(value) ? value : {}).filter(
      ([key]) => !fields.has(key)
    )
  );

const mergeRuleEvent = (
  original: unknown,
  generated: unknown
): Record<string, unknown> => {
  const originalEvent = toRecord(original);
  const generatedEvent = toRecord(generated);
  const originalParams = toRecord(originalEvent.params);
  const generatedParams = toRecord(generatedEvent.params);
  const sameAction = originalParams.action === generatedParams.action;
  const retainedParams = sameAction
    ? originalParams
    : omitManagedFields(originalParams, managedEventParams);
  const params = { ...retainedParams, ...generatedParams };
  if (sameAction && generatedParams.apply) {
    params.apply = {
      ...omitManagedFields(originalParams.apply, managedApplyFields),
      ...toRecord(generatedParams.apply),
    };
  }
  return {
    ...originalEvent,
    ...generatedEvent,
    type: originalEvent.type || generatedEvent.type,
    params,
  };
};

export const preserveRuleBody = (
  original: Rule['body'],
  generated: Rule['body'],
  changed: RuleBodyChanges
): Rule['body'] => {
  if (
    !changed.action &&
    !changed.criteria &&
    !changed.outcome &&
    !changed.checks
  ) {
    return original;
  }
  const conditionsChanged =
    changed.action || changed.criteria || changed.checks;
  return {
    ...original,
    ...(conditionsChanged
      ? {
          conditions: generated.conditions,
          when: {
            ...omitManagedFields(original.when, managedCriteriaFields),
            ...toRecord(generated.when),
          },
          tariff: {
            ...toRecord(original.tariff),
            ...toRecord(generated.tariff),
          },
          checks: generated.checks,
          ...(original.billingHistory
            ? {
                billingHistory: {
                  ...toRecord(original.billingHistory),
                  enabled: false,
                },
              }
            : {}),
        }
      : {}),
    event:
      changed.action || changed.outcome
        ? mergeRuleEvent(original.event, generated.event)
        : original.event,
  };
};

const buildRuleCheck = (assertion: AssertionRow): Record<string, unknown> => {
  const check = {
    ...omitManagedFields(assertion, managedCheckFields),
    id: assertion.id,
    field: assertion.field,
  };
  if (assertion.field === AssertionField.DuplicateClaim) return check;
  if (assertion.field === AssertionField.TariffBillingHistory) {
    const history = assertion.billingHistory;
    const window = getBillingHistoryWindow(history);
    return {
      ...check,
      window: {
        ...omitManagedFields(
          toRecord(assertion).window,
          managedBillingWindowFields
        ),
        ...window,
      },
      sameProvider: history.sameProvider,
    };
  }
  return {
    ...check,
    operator: assertion.operator,
    valuesText: assertion.valuesText,
  };
};

const buildRuleApplyPayload = (
  input: RuleBodyInput
): Record<string, unknown> => {
  const updates = input.applyUpdates.map((update) => {
    if (!timeBasedApplyFields.has(update.field)) return { ...update };
    return {
      ...update,
      value: {
        intervalMinutes: Number(input.applyIntervalMinutes),
        targetTariffs: input.firstScopeValues,
        targetMatchMode: input.firstScopeMode,
      },
    };
  });
  const payload: Record<string, unknown> = {
    updates,
    priority: input.effectiveApplyPriority,
  };
  if (input.applyBehavior) payload.behavior = input.applyBehavior;
  if (input.applyNotification) payload.notification = input.applyNotification;
  return payload;
};

const buildRuleEventParams = (
  input: RuleBodyInput
): Record<string, unknown> => {
  switch (input.ruleMode) {
    case RuleMode.ApplyData:
      return {
        category: 'claim-application',
        code: 'RULE_APPLY_DATA',
        action: 'applyData',
        apply: buildRuleApplyPayload(input),
        userMessage: `${input.effectiveRuleName} applied data.`,
      };
    case RuleMode.TariffTransforms:
      return {
        category: 'claim-application',
        code: 'RULE_APPLY_TARIFF_TRANSFORMS',
        action: 'applyTariffTransforms',
        apply: {
          appendTimeFlagsToProviderNotes: input.transformAppendFlags,
          collapseTariffsByCode: input.transformCollapseByCode,
          applyBaseTariffPercentages: input.transformApplyPercentages,
          priority: input.effectiveTransformPriority,
          timeCodes: {
            afterMidnight: input.timeCodeAfterMidnight,
            evening: input.timeCodeEvening,
            weekendDaytime: input.timeCodeWeekend,
          },
        },
        userMessage: `${input.effectiveRuleName} applied tariff transforms.`,
      };
    case 'flagOnly':
      return {
        category: 'claim-validation',
        code: 'RULE_ALERT',
        severity: RuleSeverity.Alert,
        action: 'flagOnly',
        flagLabel: input.effectiveFlagLabel,
        flagMessage: input.effectiveFlagMessage,
        flagSeverity: input.flagSeverity,
        userMessage: input.effectiveFlagMessage,
      };
    default:
      return {
        category: 'claim-validation',
        code: input.eventCode,
        severity: input.severity,
        action: 'validate',
        userMessage: input.effectiveRuleMessage,
      };
  }
};

const combineConditions = (
  operator: WhenLogic,
  conditions: RuleCondition[]
): RuleCondition => {
  if (conditions.length === 1) return conditions[0];
  return { [operator]: conditions };
};

const buildWhenConditions = (
  groups: CompleteRuleCriteriaConditions[],
  operator: WhenLogic
): RuleCondition[] => {
  const first = groups[0];
  if (groups.length === 1) {
    if (first.operator === WhenLogic.All) return first.conditions;
    return [combineConditions(first.operator, first.conditions)];
  }
  const conditions = groups.map((group) =>
    combineConditions(group.operator, group.conditions)
  );
  if (operator === WhenLogic.All) return conditions;
  return [combineConditions(operator, conditions)];
};

const buildRuleConditions = (input: RuleBodyInput): RuleCondition | null => {
  if (!hasCompleteRuleCriteria(input.criteriaConditions)) return null;
  const conditions = buildWhenConditions(
    input.criteriaConditions,
    input.whenLogic
  );
  const failures = input.validationFailureConditions;
  if (input.ruleMode !== RuleMode.Validate || failures.length === 0)
    return { all: conditions };
  return { all: [...conditions, combineConditions(WhenLogic.Any, failures)] };
};

/** Store only inputs used by this condition; the compatibility reader restores editor defaults. */
const buildRuleCriteriaClause = (
  clause: WhenClause
): Record<string, unknown> => {
  const base = {
    ...omitManagedFields(clause, managedCriteriaClauseFields),
    id: clause.id,
    scopeMode: clause.scopeMode,
    whenOperator: clause.whenOperator,
  };
  if (clause.whenOperator === 'exists' || clause.whenOperator === 'notExists')
    return base;

  switch (clause.scopeMode) {
    case ScopeMode.PatientAge:
      return {
        ...base,
        patientAgeOperator: clause.patientAgeOperator,
        ...(clause.patientAgeOperator === PatientAgeOperator.BetweenInclusive
          ? {
              patientAgeStart: clause.patientAgeStart,
              patientAgeEnd: clause.patientAgeEnd,
            }
          : { patientAgeValue: clause.patientAgeValue }),
      };
    case ScopeMode.ServiceStartTimeLocal:
      return {
        ...base,
        serviceStartTimeOperator: clause.serviceStartTimeOperator,
        ...(clause.serviceStartTimeOperator === ServiceStartTimeOperator.Equal
          ? { serviceStartTimeValue: clause.serviceStartTimeValue }
          : {
              serviceStartTimeStart: clause.serviceStartTimeStart,
              serviceStartTimeEnd: clause.serviceStartTimeEnd,
            }),
      };
    case ScopeMode.ServiceStartDayLocal:
      return {
        ...base,
        serviceStartDayOperator: clause.serviceStartDayOperator,
        ...(clause.serviceStartDayOperator === ServiceStartDayOperator.Equal
          ? { serviceStartDayValue: clause.serviceStartDayValue }
          : {
              serviceStartDayStart: clause.serviceStartDayStart,
              serviceStartDayEnd: clause.serviceStartDayEnd,
            }),
      };
    case ScopeMode.TariffBillingHistory:
      return {
        ...base,
        scopeValuesText: clause.scopeValuesText,
        billingHistory: {
          windowType: clause.billingHistory.windowType,
          sameProvider: clause.billingHistory.sameProvider,
          ...(clause.billingHistory.windowType === 'rolling'
            ? {
                amount: clause.billingHistory.amount,
                unit: clause.billingHistory.unit,
              }
            : {}),
        },
      };
    case ScopeMode.AnyClaim:
    case ScopeMode.DuplicateClaim:
    case ScopeMode.TariffHasAnestheticUnitValue:
      return withTariffExclusions(base, clause);
    default:
      return withTariffExclusions(
        {
          ...base,
          ...(clause.scopeValuesText
            ? { scopeValuesText: clause.scopeValuesText }
            : {}),
        },
        clause
      );
  }
};

/** Exclusions stay local to supported positive conditions and are omitted when empty. */
const withTariffExclusions = (
  fields: Record<string, unknown>,
  clause: WhenClause
): Record<string, unknown> => {
  if (
    !supportsTariffExclusions(clause.scopeMode, clause.whenOperator) ||
    !clause.excludedTariffValuesText.trim()
  )
    return fields;
  return {
    ...fields,
    excludedTariffValuesText: clause.excludedTariffValuesText,
  };
};

export const buildRuleBody = (input: RuleBodyInput): Rule['body'] => ({
  conditions: buildRuleConditions(input),
  event: { type: input.eventType, params: buildRuleEventParams(input) },
  tariff: { matchMode: input.firstScopeMode, values: input.firstScopeValues },
  when: {
    operator: input.whenLogic,
    groups: input.whenGroups.map((group) => ({
      ...group,
      clauses: group.clauses.map(buildRuleCriteriaClause),
    })),
  },
  checks: input.assertions.map(buildRuleCheck),
});
